import { useState } from "react"
import { Alert, Drawer, Form, Input, InputNumber, Segmented, Select, Switch, Tooltip } from "antd"
import { Info } from "lucide-react"
import {
  SPLIT_KEYS,
  type SplitKey,
  type TileConfig,
  type TileDisplay,
  type TileItem,
  type WidgetType,
} from "@csbi/contracts"
import { allowedWidgets, byId } from "@csbi/metric-catalog"
import { supportsDisplay, type DisplayKnob } from "@csbi/viz-core"
import { WIDGET_LABELS } from "@csbi/widgets"

export type SettingsPatch = {
  title: string | null
  widgetType: WidgetType
  splitBy: SplitKey[]
  display: TileDisplay
  topN: number | undefined
}

export type TileSettingsProps = {
  tile: TileItem | null
  onCancel: () => void
  onSave: (patch: SettingsPatch) => void
}

export function TileSettings({ tile, onCancel, onSave }: TileSettingsProps) {
  if (!tile) return null
  return <TileSettingsForm key={tile.id} tile={tile} onCancel={onCancel} onSave={onSave} />
}

function TileSettingsForm({ tile, onCancel, onSave }: TileSettingsProps & { tile: TileItem }) {
  const [title, setTitle] = useState(tile.title ?? "")
  const [widget, setWidget] = useState<WidgetType>(tile.widgetType)
  const [split, setSplit] = useState<SplitKey[]>([...tile.config.splitBy])
  const [display, setDisplay] = useState<TileDisplay>(tile.config.display)
  const [topN, setTopN] = useState<number | null>(tile.config.topN ?? null)

  const first = byId.get(tile.config.metrics[0] ?? "")
  const widgets = (first ? allowedWidgets(first) : [tile.widgetType]) as readonly WidgetType[]

  const splitOptions: SplitKey[] = first
    ? SPLIT_KEYS.filter((k) => k === "period" || k === "tenant" || first.dims.includes(k as never))
    : [...split]

  const isAttribute = first?.valueType === "text" || first?.valueType === "timestamp"
  const missingDims = isAttribute && first ? first.dims.filter((d) => !split.includes(d)) : []
  const blocked = missingDims.length > 0

  const set = <K extends keyof TileDisplay>(k: K, v: TileDisplay[K]) => setDisplay((d) => ({ ...d, [k]: v }))
  const has = (knob: DisplayKnob) => supportsDisplay(widget, knob)

  const apply = () =>
    onSave({
      title: title.trim() || null,
      widgetType: widget,
      splitBy: split,
      display,
      topN: topN ?? undefined,
    })

  return (
    <Drawer
      open
      width={420}
      title="Tile settings"
      onClose={onCancel}
      // Do not mask the canvas: changing chart type has to be watchable
      mask={false}
      extra={
        <a onClick={blocked ? undefined : apply} style={blocked ? { pointerEvents: "none", opacity: 0.4 } : undefined}>
          Apply
        </a>
      }
    >
      <Form layout="vertical" size="small">
        <Form.Item label="Title" help="Leave empty to use the metric name from the dictionary">
          <Input value={title} maxLength={120} onChange={(e) => setTitle(e.target.value)} placeholder={first?.name} />
        </Form.Item>

        <Form.Item label="Chart type">
          <Segmented
            block
            value={widget}
            onChange={(v) => setWidget(v as WidgetType)}
            options={widgets.map((w) => ({ value: w, label: WIDGET_LABELS[w] }))}
          />
        </Form.Item>

        <Form.Item label="Group by">
          <Select
            mode="multiple"
            value={split}
            onChange={(v) => setSplit(v as SplitKey[])}
            options={splitOptions.map((k) => ({ value: k, label: k }))}
          />
        </Form.Item>

        {blocked && (
          <Alert
            type="error"
            showIcon
            style={{ marginBottom: 16 }}
            message={`Add ${missingDims.join(", ")}`}
            description="This metric is an attribute, not a count — it has to be grouped by every dimension it declares, otherwise one arbitrary row wins and the value shown is simply wrong."
          />
        )}

        {has("legendPosition") && (
          <Form.Item
            label="Legend"
            help="Only charts with more than one series get a legend — with a single series the title already names it. One deployment means one series, so you will not see a legend until more customers are registered."
          >
            <Segmented
              block
              value={display.legendPosition}
              onChange={(v) => set("legendPosition", v as TileDisplay["legendPosition"])}
              options={[
                { value: "bottom", label: "Above the chart" },
                { value: "right", label: "Right side" },
              ]}
            />
          </Form.Item>
        )}

        {has("showDataLabels") && (
          <Form.Item label={<WithNote text="Data labels">Labelling every point makes a chart unreadable — the density gate can still drop labels even when you turn them on.</WithNote>}>
            <Segmented
              block
              value={display.showDataLabels}
              onChange={(v) => set("showDataLabels", v as TileDisplay["showDataLabels"])}
              options={
                widget === "line"
                  ? [
                      { value: "none", label: "None" },
                      { value: "endpoints", label: "Ends" },
                      { value: "extremes", label: "Min/max" },
                      { value: "all", label: "All" },
                    ]
                  : [
                      { value: "none", label: "None" },
                      { value: "all", label: "Show" },
                    ]
              }
            />
          </Form.Item>
        )}

        {has("stacked") && (
          <Form.Item label="Stack series">
            <Switch checked={display.stacked} onChange={(v) => set("stacked", v)} />
          </Form.Item>
        )}

        {has("area") && (
          <Form.Item label="Fill under the line">
            <Switch checked={display.area} onChange={(v) => set("area", v)} />
          </Form.Item>
        )}

        {has("donut") && (
          <Form.Item label="Donut (hole in the middle)">
            <Switch checked={display.donut} onChange={(v) => set("donut", v)} />
          </Form.Item>
        )}

        {has("valueFormat") && (
          <Form.Item label="Number format">
            <Select
              value={display.valueFormat}
              onChange={(v) => set("valueFormat", v as TileDisplay["valueFormat"])}
              options={(["auto", "compact", "full", "percent", "duration"] as const).map((v) => ({
                value: v,
                label: v,
              }))}
            />
          </Form.Item>
        )}

        {has("topN") && (
          <Form.Item label={<WithNote text="Top N">Cutting the list changes the total — the ranking stays trustworthy, the sum does not. The tile says so with a badge when it happens.</WithNote>}>
            <InputNumber
              min={1}
              max={200}
              value={topN}
              onChange={setTopN}
              placeholder="All"
              style={{ width: "100%" }}
            />
          </Form.Item>
        )}
      </Form>
    </Drawer>
  )
}

function WithNote({ text, children }: { text: string; children: React.ReactNode }) {
  return (
    <span style={{ display: "inline-flex", alignItems: "center", gap: 4 }}>
      {text}
      <Tooltip title={children}>
        <Info size={12} color="var(--csbi-ink-muted)" />
      </Tooltip>
    </span>
  )
}

export function applyPatch(config: TileConfig, patch: SettingsPatch): TileConfig {
  const rest = { ...config }
  delete rest.topN

  return {
    ...rest,
    widgetType: patch.widgetType,
    splitBy: patch.splitBy,
    display: patch.display,
    ...(patch.topN == null ? {} : { topN: patch.topN }),
  }
}
